import type { Theme } from "@mui/material"
import {
  BabyIcon,
  BlisterMedicineIcon,
  ConicalFlaskIcon,
  CreditCardIcon,
  FamilyIcon,
  HeartRateIcon,
  type IconUrl,
  LeafIcon,
  MedicalDeviceIcon,
  MedicinesIcon,
  PadlockIcon,
  PawIcon,
  PillsIcon,
  SyringeIcon,
} from "../components"
import {
  type ReferentialEnum,
  referentialEnumToReferentialCodeList,
} from "./referential"

// Theme selector, can be passed as is to MUI system props (sx, bgcolor, ...)
export type ThemeColor = (theme: Theme) => string

export type UniverseName = keyof typeof universeEnum
export const universeEnum = {
  RX_MEDICATIONS: {
    label: "Médicaments",
    shortLabel: "Médicaments",
    rank: 1,
    Icon: MedicinesIcon,
    color: ({ palette }) => palette.info.light,
  },
  FAMILY_HEALTH_COUNSELING: {
    label: "Santé familiale",
    shortLabel: "Santé fam.",
    rank: 2,
    Icon: FamilyIcon,
    color: ({ palette }) => palette.common.goldenYellow,
  },
  HEALTH_SERVICES: {
    label: "Missions de santé",
    shortLabel: "Mis. santé",
    rank: 3,
    Icon: HeartRateIcon,
    color: ({ palette }) => palette.common.boldTurquoise,
  },
  MEDICAL_DEVICES: {
    label: "Dispositifs médicaux",
    shortLabel: "Dispositifs méd.",
    rank: 4,
    Icon: MedicalDeviceIcon,
    color: ({ palette }) => palette.common.violet,
  },
  SUPPLEMENTS_AND_NATURAL_REMEDIES: {
    label: "Diététique",
    shortLabel: "Diét.",
    rank: 5,
    Icon: LeafIcon,
    color: ({ palette }) => palette.common.green,
  },
  HYGIENE_BEAUTY: {
    label: "Hygiène - beauté",
    shortLabel: "Hyg. Beauté",
    rank: 6,
    Icon: MedicinesIcon,
    color: ({ palette }) => palette.common.pinky,
  },
  VETERINARY: {
    label: "Vétérinaire",
    shortLabel: "Vétérinaire",
    rank: 7,
    Icon: PawIcon,
    color: ({ palette }) => palette.common.salmon,
  },
  BABY: {
    label: "Bébé",
    shortLabel: "Bébé",
    rank: 8,
    Icon: BabyIcon,
    color: ({ palette }) => palette.common.turquoise,
  },
} as const satisfies ReferentialEnum<{
  rank: number
  Icon: IconUrl
  color: ThemeColor
  shortLabel: string
}>

export const universeReferential = referentialEnumToReferentialCodeList(universeEnum)

export type SpecificUniverseName = keyof typeof specificUniverseEnum
export const specificUniverseEnum = {
  SC_HIGH_COST_MEDICATIONS: {
    label: "Produits Chers > 1930 €",
    Icon: PadlockIcon,
    rank: 5,
  },
  SC_BIOSIMILARS: { label: "Biosimilaires", Icon: PillsIcon, rank: 2 },
  SC_VACCINES: { label: "Vaccins", Icon: SyringeIcon, rank: 4 },
  SC_GENERICS: { label: "Génériques", Icon: BlisterMedicineIcon, rank: 1 },
  SC_HYBRIDS: { label: "Hybrides", Icon: ConicalFlaskIcon, rank: 3 },
  SC_PHARMACY_FEES: { label: "Honoraires", Icon: CreditCardIcon, rank: 6 },
} as const satisfies ReferentialEnum<{ rank: number; Icon: IconUrl }>
export const specificUniverseReferential =
  referentialEnumToReferentialCodeList(specificUniverseEnum)

export enum SalesDistribution {
  SALES = "sales",
  MARGIN = "margin",
}

const salesDistributionEnum = {
  [SalesDistribution.SALES]: {
    label: "Chiffre d'affaires",
  },
  [SalesDistribution.MARGIN]: {
    label: "Marge",
  },
} satisfies ReferentialEnum

export const salesDistributionReferential =
  referentialEnumToReferentialCodeList(salesDistributionEnum)
